import type { VoicemailMessageInput, VoicemailMessageUpdateInput } from "@/lib/queries";
import type { AudioFile, VoicemailMessage } from "@/lib/types";
import { pickedAudio, storeAudioUuid, type PickedAudio } from "./audio.js";
import { mediaProblem } from "./campaign.js";

/**
 * The voicemail library as the console shows and edits it (ADR-019, ADR-112 in `platform`): the
 * messages a lead's voicemail gets, and the arithmetic between the library's form and `POST`/`PUT
 * /voicemail-messages`.
 *
 * A message is one of two kinds. A **recording** plays a file. A **script** is text with fields,
 * `{{lead.first_name}}` or `{{lead.first_name|there}}`, that core-api fills for each lead: it is
 * saved and previewed here, and not played on a call until a voice speaks it.
 *
 * The rules are core-api's, in `schemas.py` and `shared/greetings.py`: the slug is
 * `_VOICEMAIL_SLUG_RE`, the languages `_check_language`, the name's bound `VoicemailMessageCreate`,
 * what a recording may play `_check_voicemail_media` — a file from the audio store by its URL
 * (`lib/audio.ts`), or, from before the store, a sound file on the switch, a `local_stream://` or a
 * `silence_stream://` — and how a field is written `TOKEN_RE` and `problems`. That a store file is
 * the tenant's own, and that a stage is one of the tenant's, is checked on the server only. The
 * server decides all of it; the copies here keep a save it would refuse off the screen and say why
 * first. Pure, for `test/voicemail.test.ts`.
 */

/** `_VOICEMAIL_SLUG_RE`: what a drop names the message by. */
export const VOICEMAIL_SLUG_RE = /^[a-z0-9][a-z0-9_-]{0,62}$/;
export const NAME_MAX = 200;

/** `_check_language`: the languages a message can be marked with. */
export const VOICEMAIL_LANGUAGES = ["en", "es"] as const;

const LANGUAGE: Record<string, string> = { en: "English", es: "Spanish" };

/** A language in words, or null for none. A code this console does not know is shown as the phone
 *  shows every code, in capitals. */
export function languageText(language: string | null): string | null {
  if (!language) return null;
  return LANGUAGE[language] ?? language.toUpperCase();
}

// --- the two kinds -----------------------------------------------------------------------------

export const VOICEMAIL_KINDS = ["script", "recording"] as const;
export type VoicemailKind = (typeof VOICEMAIL_KINDS)[number];

/** What a message is. A core-api from before scripts sends no kind, and all it has are recordings. */
export function kindOf(m: Pick<VoicemailMessage, "kind">): VoicemailKind {
  return m.kind === "script" ? "script" : "recording";
}

/** The stages a message is for; none is the general message. A core-api from before a message
 *  could list several sends its one `stage`. */
export function stagesOf(m: Pick<VoicemailMessage, "stages" | "stage">): string[] {
  if (m.stages) return [...m.stages];
  return m.stage ? [m.stage] : [];
}

/** `_SILENCE_RE`: silence the switch makes itself, for trying a drop with nothing recorded. */
const SILENCE = /^silence_stream:\/\/\d{1,6}$/;

/**
 * Why a recording cannot play this, or null when it can. Empty is refused: a line's or a campaign's
 * sound falls back on a default, and a message has nothing to fall back on. A value from before the
 * audio store passes, as the server lets it; anything else is not a file from the store.
 */
export function voicemailMediaProblem(value: string): string | null {
  const v = value.trim();
  if (v === "") return "Choose the recording it plays";
  return SILENCE.test(v) || mediaProblem(v) === null ? null : "Choose a file from the audio store";
}

// --- a script's text ---------------------------------------------------------------------------

/** `greetings.SCRIPT_MAX` and `FALLBACK_MAX`. */
export const SCRIPT_MAX = 1000;
export const FALLBACK_MAX = 60;

const FIELD_NAME = "[a-z][a-z0-9_]*(?:\\.[a-z][a-z0-9_]*)*";
/** `greetings.TOKEN_RE`: `{{name}}` or `{{name|fallback words}}`, spaces allowed inside the braces. */
const TOKEN = new RegExp(`\\{\\{\\s*(${FIELD_NAME})\\s*(?:\\|([^{}|]*))?\\}\\}`, "g");

/** A piece of a script: words, or a field with the fallback words it was given (null for none). */
export type ScriptPiece =
  | { kind: "text"; text: string }
  | { kind: "field"; name: string; fallback: string | null };

/** A script as its pieces, in order (`greetings.parse`). */
export function scriptPieces(script: string): ScriptPiece[] {
  const out: ScriptPiece[] = [];
  let at = 0;
  for (const match of script.matchAll(TOKEN)) {
    if (match.index > at) out.push({ kind: "text", text: script.slice(at, match.index) });
    out.push({ kind: "field", name: match[1] ?? "", fallback: match[2] === undefined ? null : match[2].trim() });
    at = match.index + match[0].length;
  }
  if (at < script.length) out.push({ kind: "text", text: script.slice(at) });
  return out;
}

/** The fields a script uses, each once, in the order it first uses them. */
export function scriptFields(script: string): string[] {
  const seen: string[] = [];
  for (const piece of scriptPieces(script)) {
    if (piece.kind === "field" && !seen.includes(piece.name)) seen.push(piece.name);
  }
  return seen;
}

/**
 * Why a script would be refused, or null when it would be taken (`greetings.problems`, the first of
 * them). `known` is every field the tenant's greetings may use; while it has not loaded the names
 * are not judged, so a script is never called wrong for a list that is still on its way.
 */
export function scriptProblem(script: string, known: ReadonlySet<string> | undefined): string | null {
  if (script.trim() === "") return "Write the greeting";
  if (script.length > SCRIPT_MAX) return `At most ${SCRIPT_MAX} characters`;
  const pieces = scriptPieces(script);
  if (pieces.some((p) => p.kind === "text" && /[{}]/.test(p.text))) {
    return "A field is written {{name}} or {{name|fallback words}}: check the braces";
  }
  for (const piece of pieces) {
    if (piece.kind !== "field") continue;
    if (known && !known.has(piece.name)) return `${piece.name} is not a field a greeting can use`;
    if (piece.fallback !== null && piece.fallback.length > FALLBACK_MAX) {
      return `The fallback words of ${piece.name} are at most ${FALLBACK_MAX} characters`;
    }
  }
  if (!pieces.some((p) => p.kind === "text" && p.text.trim() !== "")) {
    return "A greeting needs words of its own around its fields";
  }
  return null;
}

/** A field as a script writes it. */
export function fieldToken(name: string): string {
  return `{{${name}}}`;
}

/**
 * A script with a field put where the caret is, over whatever was selected, and where the caret
 * goes after it. A space is added on a side that would otherwise run the field into a word, so a
 * field dropped after "Hi" reads "Hi {{lead.first_name}}".
 */
export function insertField(
  script: string,
  name: string,
  start: number,
  end: number = start,
): { script: string; caret: number } {
  const from = Math.max(0, Math.min(start, end, script.length));
  const to = Math.max(from, Math.min(Math.max(start, end), script.length));
  const before = script.slice(0, from);
  const after = script.slice(to);
  const lead = before !== "" && /[^\s(¡¿]$/.test(before) ? " " : "";
  const trail = after !== "" && /^[^\s,.;:!?)]/.test(after) ? " " : "";
  const put = `${lead}${fieldToken(name)}`;
  return { script: `${before}${put}${trail}${after}`, caret: before.length + put.length };
}

// --- the table ---------------------------------------------------------------------------------

/** What a recording plays, as the library's table names it. */
export type MessageRecording =
  | PickedAudio
  /** A store URL while the audio store has not been read, or could not be: a file the page cannot
   *  name yet, which is not the same as a file gone from the store. */
  | { kind: "unread"; uuid: string };

/**
 * What a recording plays, named from the audio store as a setting's picker names it (`pickedAudio`).
 * `files` is undefined until the store has been read, and a store URL is called missing only after
 * that: a table that said "no longer in the audio store" on every row while the list loaded would be
 * saying something untrue. A script plays no file.
 */
export function messageRecording(
  media: string | null,
  files: readonly AudioFile[] | undefined,
): MessageRecording {
  const uuid = storeAudioUuid(media ?? "");
  if (uuid !== null && files === undefined) return { kind: "unread", uuid };
  return pickedAudio(media, files ?? []);
}

/** Which messages the library shows. Only an admin is sent the retired ones to choose between. */
export const LIBRARY_SHOWS = ["all", "active", "retired"] as const;
export type LibraryShow = (typeof LIBRARY_SHOWS)[number];

/**
 * The library as the page lists it: the server's order (by name), narrowed to what is shown, retired
 * messages last. Below admin core-api sends only the active ones whatever is asked, so there is
 * nothing to narrow.
 */
export function libraryRows<T extends Pick<VoicemailMessage, "active">>(
  messages: readonly T[],
  show: LibraryShow,
): T[] {
  const shown = messages.filter((m) => show === "all" || m.active === (show === "active"));
  return shown.sort((a, b) => Number(b.active) - Number(a.active));
}

/** The messages for one situation: the stages they share, in the tenant's order; none is everyone. */
export interface LibraryGroup<T> {
  key: string;
  stages: string[];
  rows: T[];
}

/**
 * The rows by situation: the messages that are for the same stages sit together, whatever their
 * language or kind, so the English and the Spanish greeting for a missed callback are neighbours.
 * `order` is the tenant's stages in their order. Groups follow it by their first stage, a stage the
 * tenant no longer has comes after those, and the general messages come last. Inside a group the
 * languages are in the form's order, English first, and a message with none after them; rows of
 * one language keep the order they came in, which is by name with the retired ones last.
 */
export function libraryGroups<T extends Pick<VoicemailMessage, "stages" | "stage" | "language">>(
  rows: readonly T[],
  order: readonly string[],
): LibraryGroup<T>[] {
  const rank = (stage: string) => {
    const at = order.indexOf(stage);
    return at === -1 ? order.length : at;
  };
  const groups = new Map<string, LibraryGroup<T>>();
  for (const row of rows) {
    const stages = stagesOf(row).sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
    const key = stages.join("+");
    const group = groups.get(key) ?? { key, stages, rows: [] };
    group.rows.push(row);
    groups.set(key, group);
  }
  const spoken = (row: T) => {
    const at = (VOICEMAIL_LANGUAGES as readonly string[]).indexOf(row.language ?? "");
    return at === -1 ? VOICEMAIL_LANGUAGES.length + Number(!row.language) : at;
  };
  // `sort` is stable, so rows of one language stay as they came.
  for (const group of groups.values()) group.rows.sort((a, b) => spoken(a) - spoken(b));
  const first = (g: LibraryGroup<T>) => {
    const stage = g.stages[0];
    return stage === undefined ? order.length + 1 : rank(stage);
  };
  return [...groups.values()].sort(
    (a, b) => first(a) - first(b) || a.stages.length - b.stages.length || a.key.localeCompare(b.key),
  );
}

// --- the form ----------------------------------------------------------------------------------

/** The library's form. No language is `""`; the recording is the URL the picker copied. A recording
 *  keeps `script` empty and a script keeps `media` empty. */
export interface VoicemailMessageValues {
  slug: string;
  name: string;
  kind: VoicemailKind;
  language: string;
  /** The lead stages it is for (ADR-061, ADR-112); none for the tenant's general message. */
  stages: string[];
  media: string;
  script: string;
  /** A script's version B (ADR-116); empty for a greeting with one version. */
  script_b: string;
}

/** A new message starts as a script: the kind this page is opened to write. */
export const NEW_VOICEMAIL_MESSAGE: VoicemailMessageValues = {
  slug: "",
  name: "",
  kind: "script",
  language: "",
  stages: [],
  media: "",
  script: "",
  script_b: "",
};

export function voicemailMessageValues(m: VoicemailMessage): VoicemailMessageValues {
  return {
    slug: m.slug,
    name: m.name,
    kind: kindOf(m),
    language: m.language ?? "",
    stages: stagesOf(m),
    media: m.media ?? "",
    script: m.script ?? "",
    script_b: m.script_b ?? "",
  };
}

/** Why a version B cannot be saved, or null. Unlike the greeting it is optional: empty is no
 *  version B, and not a mistake. Written, it is judged as the greeting is. */
export function versionBProblem(script: string, known: ReadonlySet<string> | undefined): string | null {
  return script.trim() === "" ? null : scriptProblem(script, known);
}

/** Why the form's language cannot be saved, or null: a script's words are in one. */
export function languageProblem(values: Pick<VoicemailMessageValues, "kind" | "language">): string | null {
  return values.kind === "script" && values.language === "" ? "Choose the language it is written in" : null;
}

/** What `POST /voicemail-messages` takes: trimmed, with no language as null, and only the content
 *  of its kind. A new message is active, as the server's default makes it; retiring is the list's
 *  own action. */
export function newVoicemailMessagePayload(values: VoicemailMessageValues): VoicemailMessageInput {
  const shared = {
    slug: values.slug.trim(),
    name: values.name.trim(),
    kind: values.kind,
    language: values.language || null,
    stages: [...values.stages],
  };
  if (values.kind !== "script") return { ...shared, media: values.media.trim() };
  const second = values.script_b.trim();
  return { ...shared, script: values.script.trim(), ...(second ? { script_b: second } : {}) };
}

const sameStages = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && [...a].sort().join("+") === [...b].sort().join("+");

/**
 * The fields an edit changed, as `PUT /voicemail-messages/{id}` takes them, and nothing else. Never
 * the slug — a drop names the message by it, and core-api has no way to rename one — never the
 * kind, which a message keeps, and never `active`, so saving a name does not restore a message
 * somebody retired while the form was open. A recording goes out only when it changed: core-api
 * checks every recording it is sent against the audio store, and fixing a name is no reason to run
 * that check. The stages go out as a whole list, and only when the set changed.
 */
export function voicemailMessageChanges(
  values: VoicemailMessageValues,
  m: VoicemailMessage,
): VoicemailMessageUpdateInput {
  const out: VoicemailMessageUpdateInput = {};
  if (values.name.trim() !== m.name) out.name = values.name.trim();
  if ((values.language || null) !== (m.language || null)) out.language = values.language || null;
  if (!sameStages(values.stages, stagesOf(m))) out.stages = [...values.stages];
  if (kindOf(m) === "script") {
    if (values.script.trim() !== (m.script ?? "")) out.script = values.script.trim();
    // Emptied, version B goes out as null: the greeting is one version again.
    const second = values.script_b.trim();
    if (second !== (m.script_b ?? "")) out.script_b = second || null;
  } else if (values.media.trim() !== (m.media ?? "")) {
    out.media = values.media.trim();
  }
  return out;
}

/**
 * What the library says on a greeting that uses fields it can no longer say, or null when it uses
 * none: a custom field that was unticked or archived after the greeting was saved (ADR-113). The
 * greeting is still there and still picked; such a field just has no value any more.
 */
export function unusableText(m: Pick<VoicemailMessage, "unusable_fields">): string | null {
  const names = m.unusable_fields ?? [];
  if (names.length === 0) return null;
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `Uses ${list}, which a greeting can no longer say. It says the fallback words there, or is not used.`;
}

// --- a preview ---------------------------------------------------------------------------------

/**
 * What a lead's preview says about the version they hear (ADR-116), or null when the greeting has
 * one version: which of the two, and since when nobody has reached them. `at` writes a moment for
 * the reader, the way the page writes any other.
 */
export function versionLine(
  preview: {
    message: Pick<VoicemailMessage, "script_b"> | null;
    version?: string | null;
    unanswered_since?: string | null;
    b_from?: string | null;
  },
  at: (iso: string) => string,
  now: number = Date.now(),
): string | null {
  if (!preview.message?.script_b) return null;
  const since = preview.unanswered_since ?? null;
  if (preview.version === "b") {
    return since
      ? `Version B: nobody has reached them since ${at(since)}.`
      : "Version B: nobody has reached them for a day.";
  }
  if (since === null) return "Version A. They hear version B once nobody has reached them for a day.";
  const from = preview.b_from ?? null;
  if (from !== null && Date.parse(from) > now) {
    return `Version A. Nobody has reached them since ${at(since)}, so they hear version B from ${at(from)}.`;
  }
  // A day has passed and A is still the pick: B says a field this lead has no value for.
  return `Version A, although nobody has reached them since ${at(since)}: version B says a field this lead has no value for.`;
}

/** A field's label as it reads inside a sentence about a lead: "Lead's first name" is "first name". */
export function fieldPhrase(label: string): string {
  return label.toLowerCase().replace(/^lead's /, "");
}

/**
 * What a preview says under the words, or null when the greeting would be used as it reads: the
 * fields with no value for this lead. A greeting with any is not used for the lead, so the line says
 * what to do about it. `labels` names a field by its label (`fieldPhrase`); a field it does not know
 * is named as the script writes it.
 */
export function missingText(missing: readonly string[], labels: ReadonlyMap<string, string>): string | null {
  if (missing.length === 0) return null;
  const named = missing.map((name) => labels.get(name) ?? name);
  const list = named.length === 1 ? named[0] : `${named.slice(0, -1).join(", ")} and ${named[named.length - 1]}`;
  return `There is no ${list} for this lead, so the greeting is not used for them. To use it, add fallback words, as in {{${missing[0]}|…}}.`;
}
